import type { AreaNode } from '@/lib/types/ged'

export type AreaTreeLocation = {
  node: AreaNode
  /** 1 = área de topo (raiz da árvore), 2 = filha direta de uma área de topo, e assim por diante. */
  depth: number
  /** Ancestrais do nó, da raiz até o pai direto (não inclui o próprio nó). */
  ancestors: AreaNode[]
}

export function findAreaLocation(tree: AreaNode[], id: string): AreaTreeLocation | null {
  function walk(nodes: AreaNode[], depth: number, ancestors: AreaNode[]): AreaTreeLocation | null {
    for (const node of nodes) {
      if (node.id === id) return { node, depth, ancestors }
      const found = walk(node.children, depth + 1, [...ancestors, node])
      if (found) return found
    }
    return null
  }
  return walk(tree, 1, [])
}

/**
 * Regra de navegação (spec 2026-09-12): raiz, nível 1 e nível 2 mostram os filhos como grade de
 * cards. A partir do nível 3 — e qualquer nó-folha, mesmo antes do nível 3 — mostra a listagem
 * unificada de pastas + arquivos.
 */
export function isCardsPage(depth: number, hasChildren: boolean): boolean {
  return depth <= 2 && hasChildren
}

export function countDescendantAreas(node: AreaNode): number {
  return node.children.reduce((sum, child) => sum + 1 + countDescendantAreas(child), 0)
}

export type FlatAreaOption = {
  id: string
  name: string
  path: string
}

/**
 * Lista todas as áreas da árvore como destino de upload. Qualquer área aceita arquivo direto —
 * inclusive as de nível 1 e 2 com subáreas, que mostram os arquivos próprios abaixo dos cards
 * (spec 2026-09-13). O `path` (nomes dos ancestrais + o próprio) é o rótulo do combobox.
 */
export function flattenUploadTargets(tree: AreaNode[]): FlatAreaOption[] {
  const out: FlatAreaOption[] = []

  function walk(nodes: AreaNode[], ancestorNames: string[]) {
    for (const node of nodes) {
      const path = [...ancestorNames, node.name].join(' › ')
      out.push({ id: node.id, name: node.name, path })
      walk(node.children, [...ancestorNames, node.name])
    }
  }

  walk(tree, [])
  return out
}

export type FlatArea = {
  id: string
  slug: string
  name: string
  path: string
  documentCount: number
}

/** Lista todas as áreas da árvore (qualquer profundidade), com o caminho dos ancestrais. */
export function flattenAllAreas(tree: AreaNode[]): FlatArea[] {
  const out: FlatArea[] = []

  function walk(nodes: AreaNode[], ancestorNames: string[]) {
    for (const node of nodes) {
      out.push({
        id: node.id,
        slug: node.slug,
        name: node.name,
        path: ancestorNames.join(' › '),
        documentCount: node.documentCount,
      })
      walk(node.children, [...ancestorNames, node.name])
    }
  }

  walk(tree, [])
  return out
}
